import { Button } from '@repo/ui/button'
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@repo/ui/card'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@repo/ui/form'
import { H3 } from '@repo/ui/h3'
import { useFormValidator, useT } from '@repo/ui/hooks'
import { Language } from '@repo/ui/lang'
import { Textarea } from '@repo/ui/textarea'
import { zCleanString } from '@repo/ui/utils'
import { useMemo } from 'react'

import { BeneficiaryAppColors } from '@/components/admin/BeneficiaryAppColors'
import { adminStoreActions } from '@/store/actions'
import { useStoreDispatch } from '@/store/hooks'
import { Organization, OrganizationChatSettings } from '@/store/user'

const getInformationNoticeText = (organization: Organization) => {
  const { t } = useT({ keyPrefix: 'beneficiary-app' })

  const defaultInformationNotice = (lng: Language) => t('informationNotice.description', { lng })
  const defaultWelcome = (lng: Language) => t('welcome', { lng })

  return {
    information_notice_fr:
      organization.texts?.beneficiary_app.information_notice.fr ||
      defaultInformationNotice(Language.FR),
    information_notice_en:
      organization.texts?.beneficiary_app.information_notice.en ||
      defaultInformationNotice(Language.EN),
    information_notice_es:
      organization.texts?.beneficiary_app.information_notice.es ||
      defaultInformationNotice(Language.ES),
    information_notice_pt:
      organization.texts?.beneficiary_app.information_notice.pt ||
      defaultInformationNotice(Language.PT),
    welcome_fr: organization.texts?.beneficiary_app.welcome.fr || defaultWelcome(Language.FR),
    welcome_en: organization.texts?.beneficiary_app.welcome.en || defaultWelcome(Language.EN),
    welcome_es: organization.texts?.beneficiary_app.welcome.es || defaultWelcome(Language.ES),
    welcome_pt: organization.texts?.beneficiary_app.welcome.pt || defaultWelcome(Language.PT),
  }
}

type Props = {
  organization: Organization
  handleAppSettings: {
    value: NonNullable<Organization['chatSettings']>
    onChange: (settings: OrganizationChatSettings) => void
  }
}

export function InterfaceSettings({ organization, handleAppSettings }: Props) {
  const {
    information_notice_en,
    information_notice_fr,
    information_notice_es,
    information_notice_pt,
    welcome_fr,
    welcome_en,
    welcome_es,
    welcome_pt,
  } = getInformationNoticeText(organization)
  const { t } = useT({ keyPrefix: 'admin.organization.beneficiaryApp' })

  const defaultValues = useMemo(
    () => ({
      information_notice_en,
      information_notice_fr,
      information_notice_es,
      information_notice_pt,
      welcome_en,
      welcome_fr,
      welcome_es,
      welcome_pt,
    }),
    [organization.texts],
  )
  return (
    <Card className="flex flex-1 flex-col overflow-hidden overflow-y-auto">
      <CardHeader>
        <CardTitle>{t('title')}</CardTitle>
      </CardHeader>

      <div className="border-primary-200 flex flex-col border-b">
        <div className="mx-4 xl:mx-6">
          <H3>{t('texts')}</H3>
          <p className="text-primary-500">{t('description')}</p>
        </div>
        <TextsForm defaultValues={defaultValues} />
      </div>

      <BeneficiaryAppColors handleSettings={handleAppSettings} />
    </Card>
  )
}

function TextsForm({
  defaultValues,
}: {
  defaultValues: {
    information_notice_en: string
    information_notice_fr: string
    information_notice_es: string
    information_notice_pt: string
    welcome_en: string
    welcome_fr: string
    welcome_es: string
    welcome_pt: string
  }
}) {
  const dispatch = useStoreDispatch()
  const { tc } = useT()

  const { form, isFormValid, submit, submitOnPressMetaEnter } = useFormValidator({
    defaultValues,
    onSubmit: (values) => {
      void dispatch(
        adminStoreActions.organization.update({
          fields: {
            texts: {
              beneficiary_app: {
                information_notice: {
                  en: values.information_notice_en,
                  fr: values.information_notice_fr,
                  es: values.information_notice_es,
                  pt: values.information_notice_pt,
                },
                welcome: {
                  en: values.welcome_en,
                  fr: values.welcome_fr,
                  es: values.welcome_es,
                  pt: values.welcome_pt,
                },
              },
            },
          },
        }),
      )
    },
    schema: {
      information_notice_en: zCleanString,
      information_notice_fr: zCleanString,
      information_notice_es: zCleanString,
      information_notice_pt: zCleanString,
      welcome_en: zCleanString,
      welcome_fr: zCleanString,
      welcome_es: zCleanString,
      welcome_pt: zCleanString,
    },
  })
  return (
    <Form {...form}>
      <form onSubmit={(e) => void submit(e)} onKeyDown={submitOnPressMetaEnter}>
        <CardContent>
          <FormField
            control={form.control}
            name="information_notice_en"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{field.name}</FormLabel>
                <FormControl>
                  <Textarea placeholder={defaultValues.information_notice_en} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="information_notice_fr"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{field.name}</FormLabel>
                <FormControl>
                  <Textarea placeholder={field.name} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="information_notice_es"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{field.name}</FormLabel>
                <FormControl>
                  <Textarea placeholder={field.name} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="information_notice_pt"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{field.name}</FormLabel>
                <FormControl>
                  <Textarea placeholder={field.name} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="welcome_en"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{field.name}</FormLabel>
                <FormControl>
                  <Textarea placeholder={field.name} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="welcome_fr"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{field.name}</FormLabel>
                <FormControl>
                  <Textarea placeholder={field.name} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="welcome_es"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{field.name}</FormLabel>
                <FormControl>
                  <Textarea placeholder={field.name} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="welcome_pt"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{field.name}</FormLabel>
                <FormControl>
                  <Textarea placeholder={field.name} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </CardContent>
      </form>

      <CardFooter className="justify-end">
        <Button type="submit" disabled={!isFormValid} onClick={(e) => void submit(e)}>
          {tc('update', { cfl: true })}
        </Button>
      </CardFooter>
    </Form>
  )
}
